import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, Loader2, ArrowLeft, Eye, EyeOff } from "lucide-react";

export default function AdminLogin() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password, "admin");
      toast.success("Welcome back, Admin");
      nav("/admin");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen vp-grid-bg grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-10 border-r border-white/5 bg-black/20">
        <Brand />
        <div className="max-w-md">
          <div className="h-12 w-12 grid place-items-center rounded-xl bg-emerald-500/10 text-emerald-400"><ShieldCheck className="h-6 w-6" /></div>
          <h2 className="mt-6 font-display text-3xl font-bold">Admin Control Room</h2>
          <p className="mt-3 text-slate-400 leading-relaxed">Manage users, verify UTRs, approve deposits and process payouts with a complete audit trail. Restricted access.</p>
        </div>
        <p className="text-xs text-slate-600 font-mono">VANTORPAY · SECURE ADMIN GATEWAY</p>
      </div>

      <div className="flex items-center justify-center p-6">
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white mb-8"><ArrowLeft className="h-4 w-4" /> Back home</Link>
          <div className="lg:hidden mb-6"><Brand /></div>
          <h1 className="font-display text-3xl font-extrabold">Admin Sign In</h1>
          <p className="mt-2 text-slate-400">Authorized personnel only.</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <Label htmlFor="email" className="text-slate-300">Admin Email</Label>
              <Input id="email" data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter admin email" autoComplete="off" className="mt-1.5 bg-slate-900/60 border-slate-700 h-11" />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-300">Password</Label>
              <div className="relative mt-1.5">
                <Input id="password" data-testid="admin-password-input" type={show ? "text" : "password"} required value={password}
                  onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="bg-slate-900/60 border-slate-700 h-11 pr-11" />
                <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white" data-testid="toggle-password">
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" disabled={loading} data-testid="admin-login-submit"
              className="w-full h-11 bg-emerald-500 hover:bg-emerald-400 text-[#08120e] font-semibold text-base">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Access Admin Panel"}
            </Button>
          </form>
          <p className="mt-6 text-sm text-slate-500">Are you a user? <Link to="/login" className="text-emerald-400 hover:underline">User login</Link></p>
        </motion.div>
      </div>
    </div>
  );
}
